import { creatorbase, creatorbaseQueries } from '@round/api';
import { InvalidateQueryFilters, useInfiniteQuery, useQueries, useQueryClient } from '@tanstack/react-query';
import uniq from 'lodash/uniq';
import {
    useBrandsQueriesByIds,
    useProjectsQueriesByIds,
    usePurchaseOrdersQueriesByIds,
} from '@round/creatorbase-helpers/api-hooks';
import { useMemo } from 'react';
import uniqBy from 'lodash/uniqBy';

const options = creatorbaseQueries.campaigns.options.infinite;
const campaignDetailsOptions = creatorbaseQueries.campaigns.options.detail;
const infiniteListsKey = creatorbaseQueries.campaigns.keys.infiniteLists();
const key = creatorbaseQueries.campaigns.keys.infiniteList;

type Config = Partial<ReturnType<typeof options>>;

export type UnallocatedCampaignsData = creatorbase.Campaign & {
    brand: creatorbase.Brand | null;
    project: creatorbase.Project | null;
    purchaseOrders: creatorbase.PurchaseOrder[];
};

export default function useUnallocatedCampaigns(params: creatorbase.GetCampaignsParams, config: Config = {}) {
    const queryClient = useQueryClient();
    const query = useInfiniteQuery({
        ...options(params),
        ...config,
    });

    const projectIds = query.data?.pages.map((p) => uniq(p.results.map((c) => c.project_id))) ?? [];
    const projectsResults = useProjectsQueriesByIds(projectIds);

    const brandIds =
        query.data?.pages.map((_, pageIndex) =>
            uniq(projectsResults[pageIndex]?.data?.results.map((p) => p.brand_id) ?? [])
        ) ?? [];
    const brandsResults = useBrandsQueriesByIds(brandIds);

    const campaignIds = query.data?.pages.map((p) => uniq(p.results.map((c) => c.id))) ?? [];
    const allocationsResults = useQueries({
        queries: campaignIds.map((ids) => {
            return {
                ...creatorbaseQueries.purchaseOrderLines.options.listAll({
                    campaign_id: ids.toString(),
                }),
                enabled: ids.length > 0,
                staleTime: 1000 * 60 * 5,
            };
        }),
    });

    const purchaseOrderIds = allocationsResults.map((result) =>
        uniq(result.data?.map((line) => line.purchase_order_id) ?? [])
    );
    const purchaseOrdersResults = usePurchaseOrdersQueriesByIds(purchaseOrderIds);

    const data: UnallocatedCampaignsData[] = useMemo(
        () =>
            uniqBy(
                query.data?.pages.flatMap((page, pageIndex) => {
                    const projectsResult = projectsResults[pageIndex];
                    const brandsResult = brandsResults[pageIndex];
                    const purchaseOrdersResult = purchaseOrdersResults[pageIndex];

                    return page.results.map((c) => {
                        const project = projectsResult?.data?.results.find((p) => p.id === c.project_id) ?? null;
                        const brand = brandsResult?.data?.results.find((b) => b.id === project?.brand_id) ?? null;

                        const campaignAllocations =
                            allocationsResults[pageIndex]?.data?.filter((line) => line.campaign_id === c.id) ?? [];
                        const purchaseOrders =
                            purchaseOrdersResult?.data?.results.filter((po) =>
                                campaignAllocations.some((line) => line.purchase_order_id === po.id)
                            ) ?? [];

                        return { ...c, project, brand, purchaseOrders };
                    });
                }) ?? [],
                'id'
            ),
        [allocationsResults, brandsResults, projectsResults, purchaseOrdersResults, query.data?.pages]
    );

    const setCampaign = (campaign: creatorbase.Campaign) => {
        queryClient.setQueryData<typeof query.data>(key(params), (data) => {
            if (data) {
                return {
                    ...data,
                    pages: data.pages.map((page) => {
                        return {
                            ...page,
                            results: page.results.map((c) => (c.id === campaign.id ? campaign : c)),
                        };
                    }),
                };
            }

            return data;
        });
    };

    return {
        data,
        count: query.data?.pages[0]?.count,
        pageParams: query.data?.pageParams,
        isRefetching: query.isRefetching,
        isFetching: query.isFetching,
        isPending: query.isPending,
        hasNextPage: query.hasNextPage,
        hasPreviousPage: query.hasPreviousPage,
        fetchNextPage: query.fetchNextPage,
        fetchPreviousPage: query.fetchPreviousPage,
        invalidate: (filters?: Pick<InvalidateQueryFilters, 'type'>) =>
            queryClient.invalidateQueries({ queryKey: infiniteListsKey, ...filters }),
        getIsBrandLoading: (campaign: UnallocatedCampaignsData) => {
            const pageIndex = query.data?.pages.findIndex((page) => !!page.results.find((c) => c.id === campaign.id));
            if (typeof pageIndex !== 'number') {
                return false;
            }

            return brandsResults[pageIndex]?.isFetching || brandsResults[pageIndex]?.isPending;
        },
        getIsProjectLoading: (campaign: UnallocatedCampaignsData) => {
            const pageIndex = query.data?.pages.findIndex((page) => !!page.results.find((c) => c.id === campaign.id));
            if (typeof pageIndex !== 'number') {
                return false;
            }

            return projectsResults[pageIndex]?.isFetching || projectsResults[pageIndex]?.isPending;
        },
        getArePurchaseOrdersLoading: (campaign: UnallocatedCampaignsData) => {
            const pageIndex = query.data?.pages.findIndex((page) => !!page.results.find((c) => c.id === campaign.id));
            if (typeof pageIndex !== 'number') {
                return false;
            }

            const purchaseOrdersQuery = purchaseOrdersResults[pageIndex];
            const allocationsQuery = allocationsResults[pageIndex];

            return (
                purchaseOrdersQuery?.isFetching ||
                purchaseOrdersQuery?.isLoading ||
                allocationsQuery?.isFetching ||
                allocationsQuery?.isLoading
            );
        },
        setCampaign,
        refreshCampaign: (campaignId: number) => {
            return queryClient
                .fetchQuery(campaignDetailsOptions(campaignId))
                .then((data) => setCampaign(data))
                .catch(() => {});
        },
    };
}
